import React, { useEffect, useState } from 'react';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { Link, useNavigate } from 'react-router-dom';
import { HomeIcon, BookOpenIcon, UsersIcon, BarChartIcon, CalendarIcon, SettingsIcon, MenuIcon, XIcon, SearchIcon, FilterIcon, DownloadIcon, FileTextIcon, ChevronDownIcon, ChevronUpIcon, CheckIcon, SlashIcon, EyeIcon, EditIcon, MessageSquareIcon, UserIcon } from 'lucide-react';
import { InstrumentFilterDropdown } from '../InstrumentFilterDropdown';
import { StatusFilterDropdown } from '../StatusFilterDropdown';
import { ExportDataModal } from '../ExportDataModal';
import { EditStudentPerformanceModal } from '../EditStudentPerformanceModal';
import { MessagingModal } from '../MessagingModal';
interface Student {
  id: number;
  name: string;
  instrument: string;
  grade: number;
  level: string;
  lastEvaluation: string;
  status: string;
  avatar?: string;
  notes?: string;
  flaggedForReview?: boolean;
}
export const GradesManagementPage = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('teacherDashboardTheme');
    return savedMode === 'dark';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInstruments, setSelectedInstruments] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedGradeRange, setSelectedGradeRange] = useState('all');
  const [isExportPdfModalOpen, setIsExportPdfModalOpen] = useState(false);
  const [isExportCsvModalOpen, setIsExportCsvModalOpen] = useState(false);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  // New state for modals
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isMessagingModalOpen, setIsMessagingModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  // Mock student data - in a real app, this would be fetched from your database
  const [students, setStudents] = useState<Student[]>([{
    id: 1,
    name: 'Emma Parker',
    instrument: 'Piano',
    grade: 92,
    level: 'Intermediate',
    lastEvaluation: '2023-10-15',
    status: 'active',
    avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
    notes: 'Emma has shown significant improvement in her finger placement and timing. She completed all assigned exercises and is progressing well with her current piece.'
  }, {
    id: 2,
    name: 'Michael Chen',
    instrument: 'Guitar',
    grade: 88,
    level: 'Intermediate',
    lastEvaluation: '2023-10-10',
    status: 'active',
    avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
    notes: 'Michael is making good progress with chord transitions. He should focus on improving his strumming patterns.'
  }, {
    id: 3,
    name: 'Sarah Johnson',
    instrument: 'Violin',
    grade: 95,
    level: 'Advanced',
    lastEvaluation: '2023-10-05',
    status: 'active',
    avatar: 'https://randomuser.me/api/portraits/women/68.jpg',
    notes: 'Sarah demonstrates excellent bow control and intonation. She is ready for more challenging repertoire.',
    flaggedForReview: true
  }, {
    id: 4,
    name: 'James Wilson',
    instrument: 'Piano',
    grade: 78,
    level: 'Beginner',
    lastEvaluation: '2023-09-28',
    status: 'active',
    notes: 'James needs to work on his scales and basic technique. Consider assigning more fundamental exercises.'
  }, {
    id: 5,
    name: 'Olivia Martinez',
    instrument: 'Flute',
    grade: 91,
    level: 'Advanced',
    lastEvaluation: '2023-09-20',
    status: 'active',
    notes: 'Olivia has excellent breath control and tone. She should continue working on advanced techniques.'
  }, {
    id: 6,
    name: 'Noah Brown',
    instrument: 'Drums',
    grade: 85,
    level: 'Intermediate',
    lastEvaluation: '2023-09-15',
    status: 'inactive',
    notes: 'Noah has good rhythm but needs to work on dynamics and timing consistency.'
  }, {
    id: 7,
    name: 'Sophia Davis',
    instrument: 'Voice',
    grade: 94,
    level: 'Advanced',
    lastEvaluation: '2023-09-10',
    status: 'active',
    notes: 'Sophia has a beautiful tone and excellent pitch. She should focus on expanding her repertoire.'
  }, {
    id: 8,
    name: 'Benjamin Taylor',
    instrument: 'Saxophone',
    grade: 72,
    level: 'Beginner',
    lastEvaluation: '2023-09-05',
    status: 'active',
    notes: 'Benjamin is struggling with embouchure and breath control. Recommend additional focused practice sessions.',
    flaggedForReview: true
  }, {
    id: 9,
    name: 'Isabella Thomas',
    instrument: 'Cello',
    grade: 89,
    level: 'Intermediate',
    lastEvaluation: '2023-08-30',
    status: 'inactive',
    notes: 'Isabella has good technique but needs to work on musicality and expression.'
  }, {
    id: 10,
    name: 'Ethan Garcia',
    instrument: 'Trumpet',
    grade: 81,
    level: 'Intermediate',
    lastEvaluation: '2023-08-25',
    status: 'active',
    notes: 'Ethan has improved his tone quality. He should continue working on his range and endurance.'
  }]);
  // Toggle sidebar on mobile
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  // Handle sort
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Handle edit student performance
  const handleEditClick = (student: Student) => {
    setSelectedStudent(student);
    setIsEditModalOpen(true);
  };
  // Handle messaging student
  const handleMessageClick = (student: Student) => {
    setSelectedStudent(student);
    setIsMessagingModalOpen(true);
  };
  // Handle save student performance changes
  const handleSavePerformance = (updatedStudent: Student) => {
    setStudents(students.map(s => s.id === updatedStudent.id ? updatedStudent : s));
    setIsEditModalOpen(false);
  };
  // Filter students based on search and filters
  const filteredStudents = students.filter(student => {
    // Filter by search query
    if (searchQuery && !student.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    // Filter by instrument
    if (selectedInstruments.length > 0 && !selectedInstruments.includes(student.instrument)) {
      return false;
    }
    // Filter by status
    if (selectedStatus !== 'all' && student.status !== selectedStatus) {
      return false;
    }
    // Filter by grade range
    if (selectedGradeRange !== 'all') {
      const [min, max] = selectedGradeRange.split('-').map(Number);
      if (student.grade < min || student.grade > max) {
        return false;
      }
    }
    return true;
  });
  // Sort filtered students
  const sortedStudents = [...filteredStudents].sort((a, b) => {
    if (!sortField) return 0;
    let comparison = 0;
    if (sortField === 'name') {
      comparison = a.name.localeCompare(b.name);
    } else if (sortField === 'instrument') {
      comparison = a.instrument.localeCompare(b.instrument);
    } else if (sortField === 'grade') {
      comparison = a.grade - b.grade;
    } else if (sortField === 'level') {
      comparison = a.level.localeCompare(b.level);
    } else if (sortField === 'lastEvaluation') {
      comparison = new Date(a.lastEvaluation).getTime() - new Date(b.lastEvaluation).getTime();
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Get grade color based on value
  const getGradeColor = (grade: number) => {
    if (grade >= 90) {
      return darkMode ? 'bg-green-900/30 text-green-200' : 'bg-green-100 text-green-800';
    } else if (grade >= 75) {
      return darkMode ? 'bg-blue-900/30 text-blue-200' : 'bg-blue-100 text-blue-800';
    } else if (grade >= 60) {
      return darkMode ? 'bg-yellow-900/30 text-yellow-200' : 'bg-yellow-100 text-yellow-800';
    } else {
      return darkMode ? 'bg-red-900/30 text-red-200' : 'bg-red-100 text-red-800';
    }
  };
  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return darkMode ? 'bg-green-900/30 text-green-200' : 'bg-green-100 text-green-800';
      case 'inactive':
        return darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-800';
      default:
        return darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-800';
    }
  };
  // Available fields for export
  const exportFields = ['Student Name', 'Instrument', 'Grade', 'Level', 'Last Evaluation Date', 'Status', 'Email', 'Phone Number', 'Address', 'Notes', 'Attendance Record'];
  // Listen for theme changes from the Navbar component
  useEffect(() => {
    const handleThemeChange = (event: any) => {
      setDarkMode(event.detail.darkMode);
    };
    window.addEventListener('themeChange', handleThemeChange);
    return () => {
      window.removeEventListener('themeChange', handleThemeChange);
    };
  }, []);
  return <div className={`w-full min-h-screen ${darkMode ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] text-white' : 'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-800'} font-sans transition-colors duration-300`} data-id="element-4173">
      <Navbar data-id="element-4174" />
      <div className="pt-20 pb-16 flex flex-col lg:flex-row" data-id="element-4175">
        {/* Mobile sidebar toggle */}
        <button onClick={toggleSidebar} className="lg:hidden fixed bottom-4 right-4 z-30 bg-deep-pink text-white p-3 rounded-full shadow-lg" data-id="element-4176">
          {isSidebarOpen ? <XIcon data-id="element-4177" /> : <MenuIcon data-id="element-4178" />}
        </button>

        {/* Left Sidebar */}
        <aside className={`w-64 fixed inset-y-0 pt-20 pb-16 ${darkMode ? 'bg-navy-900/90 shadow-lg' : 'bg-white shadow-md'} z-20 transform transition-all duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`} data-id="element-4179">
          <nav className="px-4 py-6" data-id="element-4180">
            <ul className="space-y-2" data-id="element-4181">
              <li data-id="element-4182">
                <Link to="/teacher-dashboard" className={`flex items-center px-4 py-3 ${darkMode ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors`} data-id="element-4183">
                  <HomeIcon className="h-5 w-5 mr-3" data-id="element-4184" />
                  Dashboard
                </Link>
              </li>
              <li data-id="element-4185">
                <Link to="/my-students" className={`flex items-center px-4 py-3 ${darkMode ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors`} data-id="element-4186">
                  <UsersIcon className="h-5 w-5 mr-3" data-id="element-4187" />
                  Students
                </Link>
              </li>
              <li data-id="element-4188">
                <Link to="/reports" className={`flex items-center px-4 py-3 ${darkMode ? 'text-deep-pink bg-navy-800/50' : 'text-deep-pink bg-red-50'} rounded-lg font-medium`} data-id="element-4189">
                  <BarChartIcon className="h-5 w-5 mr-3" data-id="element-4190" />
                  Reports
                </Link>
              </li>
              <li data-id="element-4191">
                <Link to="/my-availability" className={`flex items-center px-4 py-3 ${darkMode ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors`} data-id="element-4192">
                  <CalendarIcon className="h-5 w-5 mr-3" data-id="element-4193" />
                  Calendar
                </Link>
              </li>
              <li data-id="element-4194">
                <Link to="/user-info" className={`flex items-center px-4 py-3 ${darkMode ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors`} data-id="element-4195">
                  <SettingsIcon className="h-5 w-5 mr-3" data-id="element-4196" />
                  Settings
                </Link>
              </li>
            </ul>
          </nav>
        </aside>

        {/* Main Content */}
        <main className={`flex-1 transition-all duration-300 ease-in-out ${isSidebarOpen ? 'lg:ml-64' : 'ml-0 lg:ml-64'} px-4 py-6`} data-id="element-4197">
          <div className="max-w-7xl mx-auto" data-id="element-4198">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6" data-id="element-4199">
              <div data-id="element-4200">
                <h1 className={`text-3xl font-serif font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-4201">
                  Student Grades
                </h1>
                <p className={`mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-4202">
                  Manage and track student performance
                </p>
              </div>
              <div className="mt-4 md:mt-0 flex flex-wrap gap-2" data-id="element-4203">
                <button onClick={() => setIsExportPdfModalOpen(true)} className={`${darkMode ? 'bg-navy-900/50 hover:bg-navy-800 text-white' : 'bg-white hover:bg-gray-50 text-gray-800'} py-2 px-4 rounded-lg transition-colors flex items-center`} data-id="element-4204">
                  <FileTextIcon className="h-4 w-4 mr-2" data-id="element-4205" />
                  Export PDF
                </button>
                <button onClick={() => setIsExportCsvModalOpen(true)} className={`${darkMode ? 'bg-navy-900/50 hover:bg-navy-800 text-white' : 'bg-white hover:bg-gray-50 text-gray-800'} py-2 px-4 rounded-lg transition-colors flex items-center`} data-id="element-4206">
                  <DownloadIcon className="h-4 w-4 mr-2" data-id="element-4207" />
                  Export CSV
                </button>
              </div>
            </div>

            {/* Filters Section */}
            <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-6 mb-6 transition-colors duration-300`} data-id="element-4208">
              <div className="flex flex-col space-y-4 md:flex-row md:items-center md:space-y-0 md:space-x-4" data-id="element-4209">
                {/* Search Input */}
                <div className="relative flex-1" data-id="element-4210">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4211">
                    <SearchIcon className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-4212" />
                  </div>
                  <input type="text" placeholder="Search students..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`block w-full pl-10 pr-3 py-2 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-400' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'} focus:ring-deep-pink focus:border-deep-pink`} data-id="element-4213" />
                </div>

                {/* Instrument Filter */}
                <InstrumentFilterDropdown selectedInstruments={selectedInstruments} onChange={setSelectedInstruments} darkMode={darkMode} data-id="element-4214" />

                {/* Status Filter */}
                <StatusFilterDropdown selectedStatus={selectedStatus} onChange={setSelectedStatus} darkMode={darkMode} data-id="element-4215" />

                {/* Grade Range Filter */}
                <div className="relative" data-id="element-4216">
                  <select value={selectedGradeRange} onChange={e => setSelectedGradeRange(e.target.value)} className={`block w-full pl-4 pr-10 py-2 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} focus:ring-deep-pink focus:border-deep-pink`} data-id="element-4217">
                    <option value="all" data-id="element-4218">All Grades</option>
                    <option value="90-100" data-id="element-4219">90-100% (A)</option>
                    <option value="80-89" data-id="element-4220">80-89% (B)</option>
                    <option value="70-79" data-id="element-4221">70-79% (C)</option>
                    <option value="60-69" data-id="element-4222">60-69% (D)</option>
                    <option value="0-59" data-id="element-4223">0-59% (F)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Students Table */}
            <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm overflow-hidden transition-colors duration-300`} data-id="element-4224">
              <div className="overflow-x-auto" data-id="element-4225">
                <table className="min-w-full divide-y divide-gray-700" data-id="element-4226">
                  <thead className={`${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'}`} data-id="element-4227">
                    <tr data-id="element-4228">
                      <th scope="col" className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider cursor-pointer`} onClick={() => handleSort('name')} data-id="element-4229">
                        <div className="flex items-center" data-id="element-4230">
                          <span data-id="element-4231">Student</span>
                          {sortField === 'name' && <span className="ml-1" data-id="element-4232">
                              {sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4" data-id="element-4233" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-4234" />}
                            </span>}
                        </div>
                      </th>
                      <th scope="col" className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider cursor-pointer`} onClick={() => handleSort('instrument')} data-id="element-4235">
                        <div className="flex items-center" data-id="element-4236">
                          <span data-id="element-4237">Instrument</span>
                          {sortField === 'instrument' && <span className="ml-1" data-id="element-4238">
                              {sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4" data-id="element-4239" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-4240" />}
                            </span>}
                        </div>
                      </th>
                      <th scope="col" className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider cursor-pointer`} onClick={() => handleSort('grade')} data-id="element-4241">
                        <div className="flex items-center" data-id="element-4242">
                          <span data-id="element-4243">Grade</span>
                          {sortField === 'grade' && <span className="ml-1" data-id="element-4244">
                              {sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4" data-id="element-4245" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-4246" />}
                            </span>}
                        </div>
                      </th>
                      <th scope="col" className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider cursor-pointer`} onClick={() => handleSort('level')} data-id="element-4247">
                        <div className="flex items-center" data-id="element-4248">
                          <span data-id="element-4249">Level</span>
                          {sortField === 'level' && <span className="ml-1" data-id="element-4250">
                              {sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4" data-id="element-4251" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-4252" />}
                            </span>}
                        </div>
                      </th>
                      <th scope="col" className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider cursor-pointer`} onClick={() => handleSort('lastEvaluation')} data-id="element-4253">
                        <div className="flex items-center" data-id="element-4254">
                          <span data-id="element-4255">Last Evaluation</span>
                          {sortField === 'lastEvaluation' && <span className="ml-1" data-id="element-4256">
                              {sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4" data-id="element-4257" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-4258" />}
                            </span>}
                        </div>
                      </th>
                      <th scope="col" className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider`} data-id="element-4259">
                        Status
                      </th>
                      <th scope="col" className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider`} data-id="element-4260">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`${darkMode ? 'divide-navy-700' : 'divide-gray-200'} divide-y`} data-id="element-4261">
                    {sortedStudents.length > 0 ? sortedStudents.map(student => <tr key={student.id} className={`${darkMode ? 'hover:bg-navy-900/30' : 'hover:bg-gray-50'} transition-colors`} data-id="element-4262">
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-4263">
                            <div className="flex items-center" data-id="element-4264">
                              <div className="flex-shrink-0 h-10 w-10 rounded-full overflow-hidden" data-id="element-4265">
                                {student.avatar ? <img src={student.avatar} alt={student.name} className="h-10 w-10 rounded-full object-cover" data-id="element-4266" /> : <div className={`h-10 w-10 rounded-full flex items-center justify-center ${darkMode ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-4267">
                                    <UserIcon className={`h-6 w-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-4268" />
                                  </div>}
                              </div>
                              <div className="ml-4" data-id="element-4269">
                                <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-4270">
                                  {student.name}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-4271">
                            {student.instrument}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-4272">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getGradeColor(student.grade)}`} data-id="element-4273">
                              {student.grade}%
                            </span>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-4274">
                            {student.level}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-4275">
                            {formatDate(student.lastEvaluation)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-4276">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(student.status)}`} data-id="element-4277">
                              {student.status === 'active' ? <CheckIcon className="h-3 w-3 mr-1" data-id="element-4278" /> : <SlashIcon className="h-3 w-3 mr-1" data-id="element-4279" />}
                              {student.status.charAt(0).toUpperCase() + student.status.slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-4280">
                            <div className="flex space-x-2" data-id="element-4281">
                              <button onClick={() => navigate(`/student-grades/${student.id}`)} className="text-deep-pink hover:text-burgundy transition-colors" title="View Details" data-id="element-4282">
                                <EyeIcon className="h-5 w-5" data-id="element-4283" />
                              </button>
                              <button onClick={() => handleEditClick(student)} className="text-deep-pink hover:text-burgundy transition-colors" title="Update Grade" data-id="element-4284">
                                <EditIcon className="h-5 w-5" data-id="element-4285" />
                              </button>
                              <button onClick={() => handleMessageClick(student)} className="text-deep-pink hover:text-burgundy transition-colors" title="Send Feedback" data-id="element-4286">
                                <MessageSquareIcon className="h-5 w-5" data-id="element-4287" />
                              </button>
                            </div>
                          </td>
                        </tr>) : <tr data-id="element-4288">
                        <td colSpan={7} className={`px-6 py-4 text-center text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-4289">
                          No students found matching your filters.
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </main>
      </div>
      <Footer data-id="element-4290" />

      {/* Export PDF Modal */}
      <ExportDataModal isOpen={isExportPdfModalOpen} onClose={() => setIsExportPdfModalOpen(false)} exportType="pdf" darkMode={darkMode} availableFields={exportFields} data-id="element-4291" />

      {/* Export CSV Modal */}
      <ExportDataModal isOpen={isExportCsvModalOpen} onClose={() => setIsExportCsvModalOpen(false)} exportType="csv" darkMode={darkMode} availableFields={exportFields} data-id="element-4292" />

      {/* Edit Student Performance Modal */}
      {selectedStudent && <EditStudentPerformanceModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} onSave={handleSavePerformance} student={selectedStudent} darkMode={darkMode} data-id="element-4293" />}

      {/* Messaging Modal */}
      {selectedStudent && <MessagingModal isOpen={isMessagingModalOpen} onClose={() => setIsMessagingModalOpen(false)} student={selectedStudent} darkMode={darkMode} data-id="element-4294" />}
    </div>;
};